import { Sprite, Texture } from "pixi.js";
import { Clip } from "../clip";
import { VisualMixin, visualize } from "../mixins";
import { serializable } from "../../services";
import type { ClipType } from "../clip/clip.types";
import type { Track } from "../../tracks";
import type { Timestamp } from "../../models";
import type { PlaceholderClipProps, PlaceholderKind } from "./placeholder.interfaces";

const MEDIA_PLACEHOLDER_URL = (() => {
    try {
        const m: any = require("../../../assets/MediaPlaceholder.png");
        return String(m?.default ?? m);
    } catch {
        return "";
    }
})();
const SCREEN_PLACEHOLDER_URL = (() => {
    try {
        const m: any = require("../../../assets/ScreenPlaceholder.png");
        return String(m?.default ?? m);
    } catch {
        return "";
    }
})();
const CAMERA_PLACEHOLDER_URL = (() => {
    try {
        const m: any = require("../../../assets/CameraPlaceholder.png");
        return String(m?.default ?? m);
    } catch {
        return "";
    }
})();

export class PlaceholderClip extends VisualMixin(Clip<PlaceholderClipProps>) {
    public readonly type: ClipType = "placeholder";
    public track?: Track<PlaceholderClip>;

    @serializable()
    public label: string = "Add media";

    @serializable()
    public placeholderKind: PlaceholderKind = "media";

    private readonly sprite: Sprite;
    private lastKind?: PlaceholderKind;

    public constructor(props?: PlaceholderClipProps, id?: string) {
        super();
        if (id) this.id = id;

        // Use the provided placeholder PNGs as-is, so a placeholder is literally just a visual clip.
        this.sprite = new Sprite(Texture.EMPTY);
        this.sprite.anchor.set(0.5, 0.5);
        this.sprite.position.set(0, 0);
        this.view.addChild(this.sprite);

        if (props) {
            Object.assign(this, props);
        }

        this.on("update", this.handleUpdate.bind(this));
        this.handleUpdate();
    }

    private getPlaceholderUrl(kind: PlaceholderKind): string {
        if (kind === "screen") return SCREEN_PLACEHOLDER_URL;
        if (kind === "camera") return CAMERA_PLACEHOLDER_URL;
        return MEDIA_PLACEHOLDER_URL;
    }

    private handleUpdate(): void {
        const kind = (this.placeholderKind ?? "media") as PlaceholderKind;
        if (this.lastKind === kind) return;
        this.lastKind = kind;
        const url = this.getPlaceholderUrl(kind);
        if (!url) return;
        try {
            this.sprite.texture = Texture.from(url);
        } catch {}
    }

    @visualize
    public async update(_time: Timestamp): Promise<void> {
        // No-op. VisualMixin handles position/scale and the sprite is the entire placeholder.
    }
}
